import axios from 'axios';

const API = '/machinesim/api';

// Mirror of focas-hero's focas/tlm.py resolver, so the panel shows the SAME addresses focas-hero
// serves. The engine emits TLM_{toolKey}_COUNT/_PRESET (toolKey ∈ {T1..T6, DIE, NOZZLE, ...}); this
// maps each tool key to the raw FOCAS address its tool-life is exposed on, per the machine's config.
// Keep in sync with ~/focas/simulator/focas-wire-server/focas/tlm.py.

export const FOCAS_MODES = ['macros', 'pcodes', 'pmc'];

// Capability manifest — mirror of focas/capabilities.py DEFAULT. Stored on Machine.focasConfig.capabilities.
export const DEFAULT_CAPS = {
  identity_scrub: true,   // report as MACHINEMETRICS SIM
  program_upload: true,   // serve the running program header
  tool_groups:    false,  // raw FANUC Tool-Life-Management groups (off => tool-life via the addressing above)
  macro_r2:       true,   // serve the macro reads the adapter uses for tool-life
  pmc:            true,    // serve PMC register reads
  tool_life:      true,   // expose tool-life values
  paths:          1,       // CNC channels (TT/turn-mill = 2)
};

// UI metadata: which caps are LIVE-editable (apply in ~1s) vs boot-time (set in the sim profile).
export const CAP_META = {
  tool_life:      { label: 'Tool life',              live: true,  help: 'Expose tool-life values on the addressing above.' },
  tool_groups:    { label: 'Raw FANUC tool groups',  live: true,  help: 'Serve the captured tool-group data. Usually OFF — tool-life comes from the addressing above. Reflects on the adapter after its next reconnect.' },
  program_upload: { label: 'Program header',         live: false, help: 'Serve the running program to the adapter.' },
  macro_r2:       { label: 'Macro read',             live: false, help: 'cnc_rdmacror2 — the read path the adapter uses for tool-life.' },
  pmc:            { label: 'PMC read',               live: false, help: 'Serve PMC register reads.' },
  identity_scrub: { label: 'Identity scrub',         live: false, help: 'Hide the captured machine identity behind MACHINEMETRICS SIM.' },
};

export function capsFromConfig(focasConfig) {
  return { ...DEFAULT_CAPS, ...((focasConfig && focasConfig.capabilities) || {}) };
}

export const DEFAULT_TLM = {
  mode: 'macros',
  countBase: 600,
  presetBase: 700,
  addrType: 5,      // PMC R area
  countAddr: 100,
  presetAddr: 200,
  width: 2,
  map: {},
};

// "T3" -> 3, "5" -> 5, "DIE"/"NOZZLE" -> null
export function toolNumber(toolKey) {
  const m = /^[Tt]?(\d+)$/.exec(String(toolKey));
  return m ? parseInt(m[1], 10) : null;
}

// deterministic { toolKey -> slot }: numeric keeps its number, names take slots above the max
function slots(toolKeys) {
  const keys = [...new Set(toolKeys)].sort((a, b) => {
    const na = toolNumber(a), nb = toolNumber(b);
    if ((na === null) !== (nb === null)) return na === null ? 1 : -1;
    if (na !== null && nb !== null && na !== nb) return na - nb;
    return String(a).localeCompare(String(b));
  });
  const nums = keys.map(toolNumber).filter(n => n !== null);
  let next = (nums.length ? Math.max(...nums) : 0) + 1;
  const out = {};
  for (const k of keys) {
    const n = toolNumber(k);
    out[k] = n === null ? next++ : n;
  }
  return out;
}

// Returns { [toolKey]: { count: <Addr>, preset: <Addr> } } where Addr is a display+raw descriptor.
export function resolveAddressing(focasConfig, toolKeys) {
  const tlm = { ...DEFAULT_TLM, ...((focasConfig && focasConfig.tlm) || {}) };
  const mode = tlm.mode || 'macros';
  const explicit = tlm.map || {};
  const slot = slots(toolKeys);

  const mk = (kind, tk) => {
    const over = explicit[tk] && explicit[tk][kind.toLowerCase()];
    if (mode === 'pmc') {
      if (over && typeof over === 'object') {
        const at = over.addr_type ?? tlm.addrType, addr = over.address ?? 0, w = over.width ?? tlm.width;
        return { mode, addrType: at, address: addr, width: w, label: `R${at}/${addr}` };
      }
      const w = tlm.width;
      const abase = kind === 'COUNT' ? tlm.countAddr : tlm.presetAddr;
      const addr = abase + slot[tk] * w;
      return { mode, addrType: tlm.addrType, address: addr, width: w, label: `R${tlm.addrType}/${addr}` };
    }
    const base = kind === 'COUNT' ? tlm.countBase : tlm.presetBase;
    const number = (typeof over === 'number') ? over : base + slot[tk];
    return { mode, number, label: `#${number}` };
  };

  const out = {};
  for (const tk of Object.keys(slot)) {
    out[tk] = { count: mk('COUNT', tk), preset: mk('PRESET', tk) };
  }
  return out;
}

// Derive the tool keys the engine is emitting from a machine .state bag (TLM_{key}_COUNT/_PRESET).
export function toolKeysFromState(state) {
  const keys = new Set();
  for (const k of Object.keys(state || {})) {
    const m = /^TLM_(.+)_(COUNT|PRESET)$/.exec(k);
    if (m) keys.add(m[1]);
  }
  return [...keys];
}

export function tlmValue(state, toolKey, kind) {
  const v = (state || {})[`TLM_${toolKey}_${kind}`];
  return v === undefined ? null : Number(v);
}

export async function getFocasConfig(machineName) {
  const { data } = await axios.get(`${API}/machines/${encodeURIComponent(machineName)}/focas-config`);
  return data; // { focasConfig, toolKeys }
}

export async function saveFocasConfig(machineName, focasConfig) {
  const { data } = await axios.post(
    `${API}/machines/${encodeURIComponent(machineName)}/focas-config`, { focasConfig });
  return data;
}

// ---- tool-change scenario controls (TI test bed) --------------------------------------------
export async function getToolChange(machineName) {
  const { data } = await axios.get(`${API}/sim/${encodeURIComponent(machineName)}/tool-change`);
  return data; // { mode, downtimeSec, phase, tools }
}

// positions omitted -> reset all tools currently at preset
export async function triggerToolChange(machineName, positions) {
  const { data } = await axios.post(
    `${API}/sim/${encodeURIComponent(machineName)}/tool-change`, positions ? { positions } : {});
  return data;
}

export async function setToolChangeMode(machineName, body) {
  const { data } = await axios.post(
    `${API}/sim/${encodeURIComponent(machineName)}/tool-change-mode`, body);
  return data;
}
